// What kind of screen this is, and where a thumb can safely land on it.
//
// Imported first thing by main.ts, before the interface size and the login
// screen, because everything after that is laid out for a phone or a
// tablet as much as the world is: the 16px fields that stop iOS zooming in
// on focus, the login card that has to fit a landscape phone, the terms
// dialog. On import it puts these classes on <html>, and keeps them right
// as the window is resized, the screen turned or the pointer changed:
//
//   touch      fingers drive the viewer
//   phone      touch, on a screen with a short side of 520px or less, or a
//              window 720px wide or less (an iPad in Split View is a phone)
//   tablet     touch, and not a phone
//   landscape  or portrait, set only alongside touch
//
// phone, tablet, landscape and portrait are never set without touch, so a
// rule keyed on any of them can never reach a mouse and keyboard. A desktop
// window narrowed to 720px keeps what its (max-width: 720px) rules give it
// today. Stylesheets key on these classes rather than on (pointer: coarse),
// and code asks `device()` or listens with `onDeviceChange`, which is the
// window's `devicechange` event; the event fires only when one of the
// classes changes, not on every resize. html.touch replaces body.touch,
// which app.ts set once and nothing read.
//
// A machine with a touchscreen and a mouse, a Surface or a touch
// Chromebook, counts as touch while it is being touched and goes back to
// the desktop interface at the next mouse click, so someone who never
// touches the screen never sees a thing change. An iPad says it is a Mac
// (its Safari asks for desktop pages) and is told apart by its touch
// points. For trying the phone and tablet layouts at a desk,
// `?device=phone|tablet|desktop` on the address, or
// `localStorage.setItem("starfall.dev.device", "phone")`, forces the tier
// and touch with it.
//
// The other half is the stick zone: the rectangle in the lower left of the
// visible world where a finger landing on the world becomes the walking
// stick. It is measured against the world left uncovered, so anything that
// covers part of the screen says so with `setCovered` (a sheet, a side
// panel, a bar) and the zone moves out of its way. It is published in
// client pixels by `stickZone()` and `inStickZone()` for the touch input,
// and as CSS variables for the stylesheets, which keep their buttons out of
// it; see "phones and tablets: the shared measures" in theme.css for the
// variables and how the zoomed interface layer reads them.
//
// So the touch input asks `inStickZone` of each finger landing on the
// world, and builds or takes down the touch controls on `devicechange`
// rather than once at login. The window manager makes its windows sheets
// while `device().phone`, and the graphics defaults read `device().ipad`
// and the tier instead of guessing from the user agent. A long press is
// LONG_PRESS_MS and a tap TAP_SLOP everywhere, in the world, on the dock
// and on a list row alike, so that they all feel the same. On a touch
// screen the event has already fired at import, before anything could
// listen, so anything starting later reads `device()` first and then
// listens.

/** A press this long without moving is a long press, in milliseconds. One value for every long press, from the world's menu to a dock button. */
export const LONG_PRESS_MS = 450;
/** How far a finger may wander, in screen pixels, and still count as a tap. */
export const TAP_SLOP = 12;
/**
 * Room left along the left edge, in screen pixels, where a finger never
 * starts the stick: that is where Android's back gesture and Safari's
 * swipe back begin, and a thumb resting on the stick comes close to it.
 */
export const EDGE_GUARD = 24;
/** The stick zone, as parts of the uncovered world: its width from the left, and its height up from the bottom. */
export const STICK_ZONE = { width: 0.4, height: 0.5 } as const;
/**
 * The narrowest strip of world the stick and Jump with its buttons fit in
 * side by side, in screen pixels: the narrowest phone held upright. A
 * sheet at the side that leaves less, the map on a phone on its side,
 * puts both away, and the strip is for looking about.
 */
export const WALK_ROOM = 320;
/** A touch screen whose short side is no longer than this is a phone, in CSS pixels. */
export const PHONE_SHORT_SIDE = 520;
/** A touch screen in a window no wider than this is laid out as a phone, in CSS pixels. */
export const PHONE_MAX_WIDTH = 720;

export type Tier = "phone" | "tablet" | "desktop";

export interface Device {
  /** Fingers drive the viewer. */
  touch: boolean;
  tier: Tier;
  phone: boolean;
  tablet: boolean;
  /** Wider than tall. Only meaningful with touch; html carries it only then. */
  landscape: boolean;
  /** An iPad, whatever its Safari claims to be. */
  ipad: boolean;
  /** A touchscreen beside a finer pointer: touch only while it is being touched. */
  hybrid: boolean;
  /** Set by ?device= or the dev.device setting rather than worked out. */
  forced: boolean;
}

/** What the browser says about itself, everything `classify` needs. */
export interface DeviceEnv {
  /** The window's size in CSS pixels, innerWidth and innerHeight. */
  width: number;
  height: number;
  /** (pointer: coarse): the primary pointer is a finger. */
  coarse: boolean;
  /** (any-pointer: coarse): some pointer is a finger. */
  anyCoarse: boolean;
  maxTouchPoints: number;
  userAgent: string;
  /** On a hybrid, what the last real press came from. */
  lastPointer?: "touch" | "mouse" | null;
  forced?: Tier | null;
}

/** Distances in from each edge of the screen, in screen pixels. */
export interface Edges {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** A rectangle in client coordinates, the space PointerEvent.clientX and clientY are in. */
export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

const NO_EDGES: Edges = { top: 0, right: 0, bottom: 0, left: 0 };

/**
 * An iPad. Since iPadOS 13 its Safari sends a Mac's user agent, so the
 * only tell is that a Mac has no touchscreen.
 */
export function isIPad(userAgent: string, maxTouchPoints: number): boolean {
  return /iPad/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1);
}

/** Whether a touch screen of this size gets the phone layout. */
export function isPhoneSize(width: number, height: number): boolean {
  return Math.min(width, height) <= PHONE_SHORT_SIDE || width <= PHONE_MAX_WIDTH;
}

/** A forced tier from the address or the setting, as typed: `phone`, or `"phone"` as saveSetting writes it. */
export function parseForcedTier(raw: string | null | undefined): Tier | null {
  const value = (raw ?? "").trim().replace(/^"|"$/g, "").toLowerCase();
  return value === "phone" || value === "tablet" || value === "desktop" ? value : null;
}

/** The device, from what the browser says. */
export function classify(env: DeviceEnv): Device {
  const ipad = isIPad(env.userAgent, env.maxTouchPoints);
  const fingers = env.maxTouchPoints > 0;
  const hybrid = !ipad && fingers && env.anyCoarse && !env.coarse;
  let touch = ipad || (fingers && env.coarse) || (hybrid && env.lastPointer === "touch");
  if (env.forced) touch = env.forced !== "desktop";
  let tier: Tier = "desktop";
  if (touch) tier = env.forced && env.forced !== "desktop" ? env.forced : isPhoneSize(env.width, env.height) ? "phone" : "tablet";
  return {
    touch,
    tier,
    phone: tier === "phone",
    tablet: tier === "tablet",
    landscape: env.width > env.height,
    ipad,
    hybrid,
    forced: !!env.forced,
  };
}

/**
 * Whether the classes on <html> differ between two readings, which is all
 * a devicechange reports. Landscape and portrait are set only with touch,
 * so a desktop window snapped to half a screen, and so taller than wide,
 * changes nothing.
 */
export function deviceChanged(was: Device, now: Device): boolean {
  return now.touch !== was.touch || now.tier !== was.tier || (now.touch && now.landscape !== was.landscape);
}

/**
 * Whether a resize is only the on-screen keyboard squeezing the window.
 * Most browsers now lay the keyboard over the page, but one that shrinks
 * the window instead would otherwise turn a tablet into a phone, or a
 * portrait screen into a landscape one, for as long as you type.
 */
export function keyboardSqueeze(settled: { width: number; height: number } | null, next: { width: number; height: number }, editing: boolean): boolean {
  return !!settled && editing && next.width === settled.width && next.height < settled.height;
}

/** Whether focusing this brings up a keyboard, or a picker in its place. */
export function isEditable(target: EventTarget | Element | null | undefined): boolean {
  const node = target as { tagName?: string; getAttribute?: (name: string) => string | null; readOnly?: boolean; isContentEditable?: boolean } | null;
  if (!node || typeof node.tagName !== "string") return false;
  const tag = node.tagName.toUpperCase();
  if (tag === "TEXTAREA") return !node.readOnly;
  if (tag === "SELECT") return true;
  if (tag === "INPUT") {
    const type = (node.getAttribute?.("type") ?? "text").toLowerCase();
    return !NO_KEYBOARD.has(type) && !node.readOnly;
  }
  return node.isContentEditable === true;
}

const NO_KEYBOARD = new Set(["button", "checkbox", "color", "file", "hidden", "image", "radio", "range", "reset", "submit"]);

/**
 * The stick zone for a screen of this size, with these parts covered by
 * the interface and these safe-area insets. Its left side keeps clear of
 * the edge the back gesture starts from, and its foot clear of the home
 * indicator; it comes back empty when there is no world left to put it in.
 */
export function stickZoneFor(width: number, height: number, covered: Edges, insets: Edges): Rect {
  if (worldTooNarrow(width, covered)) return { left: 0, top: 0, right: 0, bottom: 0 };
  const world = { left: covered.left, top: covered.top, right: width - covered.right, bottom: height - covered.bottom };
  const across = world.right - world.left;
  const down = world.bottom - world.top;
  const left = Math.max(world.left, insets.left, EDGE_GUARD);
  const right = world.left + across * STICK_ZONE.width;
  const top = world.top + down * (1 - STICK_ZONE.height);
  const bottom = Math.min(world.bottom, height - insets.bottom);
  if (right <= left || bottom <= top) return { left: 0, top: 0, right: 0, bottom: 0 };
  return { left, top, right, bottom };
}

/**
 * Whether what covers the sides of a screen this wide leaves too narrow a
 * strip of world for the stick and the buttons (WALK_ROOM). Only what
 * covers a side counts: a screen that is narrow by itself keeps them,
 * squeezed as they may be, since without them there is no walking at all.
 */
export function worldTooNarrow(width: number, covered: Edges): boolean {
  return covered.left + covered.right > 0 && width - covered.left - covered.right < WALK_ROOM;
}

/** Whether a point falls inside a rectangle, its far edges excluded. */
export function inRect(r: Rect, x: number, y: number): boolean {
  return x >= r.left && x < r.right && y >= r.top && y < r.bottom;
}

// ----- the live device -----

const live = typeof window !== "undefined" && typeof document !== "undefined";

let current: Device = classify({ width: 1280, height: 720, coarse: false, anyCoarse: false, maxTouchPoints: 0, userAgent: "" });
let settled: { width: number; height: number } | null = null;
let lastPointer: "touch" | "mouse" | null = null;
const covering = new Map<string, Partial<Edges>>();
let insets: Edges = NO_EDGES;
let zone: Rect = { left: 0, top: 0, right: 0, bottom: 0 };
let probe: HTMLElement | null = null;

/** The device as it is now. */
export function device(): Device {
  return current;
}

export function isTouch(): boolean {
  return current.touch;
}

export function isPhone(): boolean {
  return current.phone;
}

export function isTablet(): boolean {
  return current.tablet;
}

/** Be told whenever touch or the tier changes, or a touch screen turns. Returns a way to stop. */
export function onDeviceChange(fn: (device: Device) => void): () => void {
  if (!live) return () => undefined;
  const listener = (e: Event) => fn((e as CustomEvent<Device>).detail);
  window.addEventListener("devicechange", listener);
  return () => window.removeEventListener("devicechange", listener);
}

/**
 * Say that part of the screen is covered, so the world left visible, and
 * the stick zone inside it, shrink away from it. `owner` names who is
 * covering (`"sheets"`, `"tabbar"`), so each says only its own; null takes
 * it back. Distances are in from each edge, in screen pixels.
 */
export function setCovered(owner: string, edges: Partial<Edges> | null): void {
  if (edges) covering.set(owner, edges);
  else covering.delete(owner);
  publishZone();
}

/** How far in from each edge the interface covers the world, the most anyone has said. */
export function coveredEdges(): Edges {
  const out = { ...NO_EDGES };
  for (const edges of covering.values()) {
    out.top = Math.max(out.top, edges.top ?? 0);
    out.right = Math.max(out.right, edges.right ?? 0);
    out.bottom = Math.max(out.bottom, edges.bottom ?? 0);
    out.left = Math.max(out.left, edges.left ?? 0);
  }
  return out;
}

/** The part of the screen showing the world, in client pixels. */
export function worldRect(): Rect {
  const c = coveredEdges();
  const { width, height } = screenSize();
  return { left: c.left, top: c.top, right: width - c.right, bottom: height - c.bottom };
}

/** The safe-area insets, from the same variables the stylesheets read, so a simulated notch moves both. */
export function safeInsets(): Edges {
  return insets;
}

/** Where a finger landing on the world becomes the stick, in client pixels; empty without touch. */
export function stickZone(): Rect {
  return zone;
}

export function inStickZone(x: number, y: number): boolean {
  return inRect(zone, x, y);
}

/** Read everything again: after a forced tier is changed from the console, say. */
export function refreshDevice(): void {
  if (!live) return;
  const { width, height } = screenSize();
  if (!keyboardSqueeze(settled, { width, height }, isEditable(document.activeElement))) settled = { width, height };
  const next = classify({
    width: settled?.width ?? width,
    height: settled?.height ?? height,
    coarse: matches("(pointer: coarse)"),
    anyCoarse: matches("(any-pointer: coarse)"),
    maxTouchPoints: navigator.maxTouchPoints || 0,
    userAgent: navigator.userAgent,
    lastPointer,
    forced: forcedTier(),
  });
  const changed = deviceChanged(current, next);
  current = next;
  applyClasses(next);
  if (next.touch) insets = measureInsets();
  publishZone();
  if (changed) window.dispatchEvent(new CustomEvent<Device>("devicechange", { detail: next }));
}

function screenSize(): { width: number; height: number } {
  return live ? { width: window.innerWidth || 1280, height: window.innerHeight || 720 } : { width: 1280, height: 720 };
}

function matches(query: string): boolean {
  try {
    return matchMedia(query).matches;
  } catch {
    return false;
  }
}

function forcedTier(): Tier | null {
  const asked = parseForcedTier(new URLSearchParams(location.search).get("device"));
  if (asked) return asked;
  try {
    return parseForcedTier(localStorage.getItem("starfall.dev.device"));
  } catch {
    return null;
  }
}

function applyClasses(d: Device): void {
  const html = document.documentElement.classList;
  html.toggle("touch", d.touch);
  html.toggle("phone", d.phone);
  html.toggle("tablet", d.tablet);
  html.toggle("landscape", d.touch && d.landscape);
  html.toggle("portrait", d.touch && !d.landscape);
}

/**
 * The safe-area insets as pixels. env() cannot be read from script, so a
 * hidden element is padded with the same variables the stylesheets use
 * and its padding read back.
 */
function measureInsets(): Edges {
  if (!document.body) return NO_EDGES;
  if (!probe) {
    probe = document.createElement("div");
    probe.setAttribute("aria-hidden", "true");
    probe.style.cssText =
      "position:fixed;left:0;top:0;width:0;height:0;visibility:hidden;pointer-events:none;" +
      "padding:var(--screen-inset-top,0px) var(--screen-inset-right,0px) var(--screen-inset-bottom,0px) var(--screen-inset-left,0px)";
    document.body.append(probe);
  }
  const style = getComputedStyle(probe);
  return {
    top: parseFloat(style.paddingTop) || 0,
    right: parseFloat(style.paddingRight) || 0,
    bottom: parseFloat(style.paddingBottom) || 0,
    left: parseFloat(style.paddingLeft) || 0,
  };
}

const ZONE_VARS = [
  "--screen-world-top", "--screen-world-right", "--screen-world-bottom", "--screen-world-left",
  "--screen-stick-left", "--screen-stick-bottom", "--screen-stick-width", "--screen-stick-height",
];

/**
 * The world and the stick zone as CSS variables on <html>, in screen
 * pixels; theme.css turns them into --world-* and --stick-zone-* for each
 * layer. With them goes html.world-narrow, while a sheet at the side
 * leaves too little world to walk in. Only with touch: a desktop page is
 * left exactly as it was.
 */
function publishZone(): void {
  if (!live) return;
  const style = document.documentElement.style;
  if (!current.touch) {
    zone = { left: 0, top: 0, right: 0, bottom: 0 };
    for (const name of ZONE_VARS) style.removeProperty(name);
    document.documentElement.classList.toggle("world-narrow", false);
    return;
  }
  const { width, height } = screenSize();
  const c = coveredEdges();
  zone = stickZoneFor(width, height, c, insets);
  document.documentElement.classList.toggle("world-narrow", worldTooNarrow(width, c));
  const px = (n: number) => `${Math.round(n)}px`;
  style.setProperty("--screen-world-top", px(c.top));
  style.setProperty("--screen-world-right", px(c.right));
  style.setProperty("--screen-world-bottom", px(c.bottom));
  style.setProperty("--screen-world-left", px(c.left));
  style.setProperty("--screen-stick-left", px(zone.left));
  style.setProperty("--screen-stick-bottom", px(height - zone.bottom));
  style.setProperty("--screen-stick-width", px(zone.right - zone.left));
  style.setProperty("--screen-stick-height", px(zone.bottom - zone.top));
}

/**
 * A notch for trying layouts at a desk: `?safe=47,0,21,0` sets the four
 * insets, top, right, bottom and left as a CSS shorthand gives them.
 */
function simulatedInsets(): void {
  const asked = new URLSearchParams(location.search).get("safe");
  if (!asked) return;
  const [top = 0, right = top, bottom = top, left = right] = asked.split(",").map((n) => Math.max(0, Number(n) || 0));
  const style = document.documentElement.style;
  style.setProperty("--screen-inset-top", `${top}px`);
  style.setProperty("--screen-inset-right", `${right}px`);
  style.setProperty("--screen-inset-bottom", `${bottom}px`);
  style.setProperty("--screen-inset-left", `${left}px`);
}

/** Watch for anything that could change the answer. */
function start(): void {
  const root = document.documentElement.style;
  root.setProperty("--long-press", `${LONG_PRESS_MS}ms`);
  root.setProperty("--screen-tap-slop", `${TAP_SLOP}px`);
  simulatedInsets();

  const again = () => refreshDevice();
  window.addEventListener("resize", again);
  window.addEventListener("orientationchange", again);
  screen.orientation?.addEventListener?.("change", again);
  for (const query of ["(pointer: coarse)", "(any-pointer: coarse)", "(orientation: landscape)"]) {
    try {
      matchMedia(query).addEventListener("change", again);
    } catch {
      // An old engine without media query listeners: resizes still count.
    }
  }
  // On a hybrid, whatever pressed last decides. Seen in the capture phase,
  // ahead of everything else on the page, so the touch controls are there
  // for the very press that asked for them. A pen is neither: it writes
  // and points as a mouse does but is used on the screen as a finger is.
  window.addEventListener(
    "pointerdown",
    (e) => {
      if (!current.hybrid || current.forced) return;
      const kind = e.pointerType === "touch" ? "touch" : e.pointerType === "mouse" ? "mouse" : null;
      if (!kind || kind === lastPointer) return;
      lastPointer = kind;
      refreshDevice();
    },
    { capture: true, passive: true },
  );
  refreshDevice();
}

if (live) start();

declare global {
  interface WindowEventMap {
    devicechange: CustomEvent<Device>;
  }
}
